import { useState, useCallback, useMemo } from 'react'
import { PencilIcon } from 'lucide-react'
import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { type ExtractedRiskReductionMeasure } from '@/shared/hooks/api/standards'
import { useUpdateUserStandardRiskReductionMeasures } from '@/shared/hooks/api/userStandards'
import {
  resolveLocalizedRiskReductionMeasureField,
  resolveRiskReductionMeasureAvailableLocales,
} from '@/shared/lib/standardEntityLocalizations'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useEntityDisplayLocales } from '../hooks/useEntityDisplayLocales'
import { useStandardSectionExtraction } from '../hooks/useStandardSectionExtraction'
import type { StandardDetailSectionProps } from '../standardDetailSectionProps'
import { isRiskReductionMeasuresConfirmedEmpty } from '../utils'
import { SaveAsExtractionGoldButton } from './SaveAsExtractionGoldButton'
import { StandardEntityLocaleToggle } from './StandardEntityLocaleToggle'
import { StandardSectionEmptyState } from './StandardSectionEmptyState'
import { StandardSectionExtractControls } from './StandardSectionExtractControls'

export type StandardRiskReductionMeasuresSectionProps =
  StandardDetailSectionProps

type RrmFormData = {
  name: string
  type: 'ISD' | 'SCP' | 'PPE' | 'OI'
  description: string
  clauseReference: string
  dependsOnControlSystem?: boolean
}

type DialogState = { type: 'edit'; rrmId: string } | { type: 'add' } | null

const rrmTypeOptions: Array<RrmFormData['type']> = ['ISD', 'SCP', 'PPE', 'OI']

const emptyFormData: RrmFormData = {
  name: '',
  type: 'SCP',
  description: '',
  clauseReference: '',
  dependsOnControlSystem: false,
}

function rrmToFormData(rrm: ExtractedRiskReductionMeasure): RrmFormData {
  return {
    name: rrm.name?.trim() ?? '',
    type: rrm.type,
    description: rrm.description?.trim() ?? '',
    clauseReference: rrm.clauseReference?.trim() ?? '',
    dependsOnControlSystem:
      rrm.type === 'SCP' ? (rrm.dependsOnControlSystem ?? false) : false,
  }
}

function formDataToRrm(
  formData: RrmFormData
): Omit<ExtractedRiskReductionMeasure, 'id'> {
  const clauseReference = formData.clauseReference.trim() || undefined
  if (formData.type === 'SCP') {
    return {
      type: 'SCP',
      name: formData.name.trim(),
      description: formData.description.trim(),
      dependsOnControlSystem: formData.dependsOnControlSystem,
      clauseReference,
    }
  }
  return {
    type: formData.type,
    name: formData.name.trim(),
    description: formData.description.trim(),
    clauseReference,
  }
}

function buildRiskReductionMeasureWithId(
  rrm: Omit<ExtractedRiskReductionMeasure, 'id'>,
  id: string
): ExtractedRiskReductionMeasure {
  if (rrm.type === 'SCP') {
    return {
      id,
      name: rrm.name,
      type: 'SCP',
      description: rrm.description,
      dependsOnControlSystem: rrm.dependsOnControlSystem ?? false,
      clauseReference: rrm.clauseReference,
    }
  }

  return {
    id,
    name: rrm.name,
    type: rrm.type,
    description: rrm.description,
    clauseReference: rrm.clauseReference,
  }
}

function isFormValid(formData: RrmFormData): boolean {
  return (
    formData.name.trim().length > 0 &&
    formData.description.trim().length > 0 &&
    rrmTypeOptions.includes(formData.type)
  )
}

export function StandardRiskReductionMeasuresSection({
  standard,
  hasPdf,
  isPdfUploading,
  onUploadPdf,
}: StandardRiskReductionMeasuresSectionProps) {
  const extraction = useStandardSectionExtraction(
    standard,
    'riskReductionMeasures'
  )
  const { t } = useTranslation('standardsLibrary')
  const { getDisplayLocale, selectItemLocale } = useEntityDisplayLocales()
  const updateRiskReductionMeasures =
    useUpdateUserStandardRiskReductionMeasures(standard.id)

  const [dialogState, setDialogState] = useState<DialogState>(null)
  const [formData, setFormData] = useState<RrmFormData>(emptyFormData)

  const currentRiskReductionMeasures =
    standard.riskReductionMeasures?.measures ?? []
  const confirmedEmpty = isRiskReductionMeasuresConfirmedEmpty(standard)

  const openEditDialog = useCallback((rrm: ExtractedRiskReductionMeasure) => {
    setFormData(rrmToFormData(rrm))
    setDialogState({ type: 'edit', rrmId: rrm.id })
  }, [])

  const openAddDialog = useCallback(() => {
    setFormData(emptyFormData)
    setDialogState({ type: 'add' })
  }, [])

  const closeDialog = useCallback(() => {
    setDialogState(null)
  }, [])

  const updateRrm = useCallback(() => {
    if (dialogState?.type !== 'edit' || !isFormValid(formData)) {
      return
    }
    const editIndex = currentRiskReductionMeasures.findIndex(
      (rrm) => rrm.id === dialogState.rrmId
    )
    if (editIndex === -1) {
      return
    }
    const normalized = formDataToRrm(formData)
    const updated = currentRiskReductionMeasures.slice()
    const existing = currentRiskReductionMeasures[editIndex]
    updated[editIndex] = {
      ...buildRiskReductionMeasureWithId(normalized, existing.id),
      localizations: existing.localizations,
    }
    updateRiskReductionMeasures.mutate(
      { measures: updated },
      { onSuccess: closeDialog }
    )
  }, [
    dialogState,
    formData,
    currentRiskReductionMeasures,
    updateRiskReductionMeasures,
    closeDialog,
  ])

  const createRrm = useCallback(() => {
    if (!isFormValid(formData)) {
      return
    }
    const normalized = formDataToRrm(formData)
    const updated = [
      ...currentRiskReductionMeasures,
      buildRiskReductionMeasureWithId(normalized, crypto.randomUUID()),
    ]
    updateRiskReductionMeasures.mutate(
      { measures: updated },
      { onSuccess: closeDialog }
    )
  }, [
    formData,
    currentRiskReductionMeasures,
    updateRiskReductionMeasures,
    closeDialog,
  ])

  const deleteCurrentRrm = useCallback(() => {
    if (dialogState?.type !== 'edit') {
      return
    }
    const updated = currentRiskReductionMeasures.filter(
      (rrm) => rrm.id !== dialogState.rrmId
    )
    updateRiskReductionMeasures.mutate(
      { measures: updated },
      { onSuccess: closeDialog }
    )
  }, [
    dialogState,
    currentRiskReductionMeasures,
    updateRiskReductionMeasures,
    closeDialog,
  ])

  const isDialogOpen = dialogState !== null
  const isEdit = dialogState?.type === 'edit'
  const dialogTitle = isEdit
    ? t('detail.editRiskReductionMeasure')
    : t('detail.addRiskReductionMeasure')
  const isSaveDisabled = useMemo(
    () => updateRiskReductionMeasures.isPending || !isFormValid(formData),
    [updateRiskReductionMeasures.isPending, formData]
  )

  const hasCurrentRiskReductionMeasures =
    currentRiskReductionMeasures.length > 0

  return (
    <>
      <Card inert={extraction.extractDialogOpen || undefined}>
        <CardHeader className='pb-2'>
          <div className='flex flex-row items-center justify-between gap-4'>
            <CardTitle>{t('detail.riskReductionMeasures')}</CardTitle>
            <div className='flex flex-wrap items-center gap-2'>
              <StandardSectionExtractControls
                hasPdf={hasPdf}
                extraction={extraction}
              />
              <SaveAsExtractionGoldButton
                userStandardId={standard.id}
                organizationId={standard.organizationId}
                extractionType='RISK_REDUCTION_MEASURES'
              />
              {hasCurrentRiskReductionMeasures && (
                <Button variant='outline' size='sm' onClick={openAddDialog}>
                  {t('detail.addRiskReductionMeasure')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {!hasCurrentRiskReductionMeasures ? (
          <StandardSectionEmptyState
            icon={<ShieldCheck className='size-10' aria-hidden />}
            title={
              confirmedEmpty
                ? t('detail.empty.riskReductionMeasures.confirmedEmptyTitle')
                : t('detail.empty.riskReductionMeasures.title')
            }
            description={
              confirmedEmpty
                ? t(
                    'detail.empty.riskReductionMeasures.confirmedEmptyDescription'
                  )
                : hasPdf
                  ? t('detail.empty.riskReductionMeasures.descriptionWithPdf')
                  : t('detail.empty.riskReductionMeasures.descriptionNoPdf')
            }
            hasPdf={hasPdf}
            isPdfUploading={isPdfUploading}
            extraction={extraction}
            onUploadPdf={onUploadPdf}
            onManualAction={openAddDialog}
            manualActionLabel={t('detail.addRiskReductionMeasure')}
          />
        ) : (
          <ul className='divide-y border-t pt-2'>
            {currentRiskReductionMeasures.map((rrm) => {
              const availableLocales =
                resolveRiskReductionMeasureAvailableLocales(rrm)
              const itemLocale = getDisplayLocale(rrm.id)
              return (
                <li
                  key={rrm.id}
                  className='flex flex-wrap items-start justify-between gap-2 px-4 py-2 first:pt-0'
                >
                  <div className='min-w-0 flex-1 space-y-1'>
                    <p className='font-medium'>
                      {rrm.clauseReference != null &&
                        rrm.clauseReference.trim() !== '' && (
                          <span className='text-muted-foreground'>
                            {rrm.clauseReference.trim()}.{' '}
                          </span>
                        )}
                      {resolveLocalizedRiskReductionMeasureField(
                        rrm,
                        itemLocale,
                        'name'
                      )}{' '}
                      <span className='text-muted-foreground'>
                        ({rrm.type})
                      </span>
                    </p>
                    {rrm.type === 'SCP' && (
                      <p className='text-sm text-muted-foreground'>
                        {t('detail.dependsOnControlSystem')}:{' '}
                        {rrm.dependsOnControlSystem
                          ? t('detail.extractPreview.yes')
                          : t('detail.extractPreview.no')}
                      </p>
                    )}
                    <p className='text-sm text-muted-foreground'>
                      {resolveLocalizedRiskReductionMeasureField(
                        rrm,
                        itemLocale,
                        'description'
                      )}
                    </p>
                  </div>
                  <div className='flex shrink-0 items-start gap-2'>
                    <StandardEntityLocaleToggle
                      availableLocales={availableLocales}
                      selectedLocale={itemLocale}
                      onSelectLocale={(locale) =>
                        selectItemLocale(rrm.id, locale)
                      }
                    />
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => openEditDialog(rrm)}
                      aria-label={t('detail.edit')}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
      >
        <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className='space-y-4'>
            {updateRiskReductionMeasures.isError && (
              <Alert variant='destructive'>
                <AlertDescription>
                  {updateRiskReductionMeasures.error?.message ??
                    t('detail.saveError')}
                </AlertDescription>
              </Alert>
            )}

            <div className='space-y-2'>
              <Label htmlFor='rrm-name'>{t('detail.rrmName')}</Label>
              <Input
                id='rrm-name'
                value={formData.name}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='rrm-clause-reference'>
                {t('detail.rrmClauseReference')}
              </Label>
              <Input
                id='rrm-clause-reference'
                value={formData.clauseReference}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    clauseReference: event.target.value,
                  }))
                }
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='rrm-type'>{t('detail.rrmType')}</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setFormData((prev) => ({
                    ...prev,
                    type: value as RrmFormData['type'],
                    dependsOnControlSystem:
                      value === 'SCP'
                        ? (prev.dependsOnControlSystem ?? false)
                        : false,
                  }))
                }}
              >
                <SelectTrigger id='rrm-type'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rrmTypeOptions.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'SCP' && (
              <div className='space-y-2'>
                <Label>{t('detail.dependsOnControlSystem')}</Label>
                <RadioGroup
                  value={formData.dependsOnControlSystem ? 'yes' : 'no'}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      dependsOnControlSystem: value === 'yes',
                    }))
                  }
                  className='flex gap-4'
                >
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='yes' id='standard-rrm-depends-yes' />
                    <Label
                      htmlFor='standard-rrm-depends-yes'
                      className='cursor-pointer font-normal'
                    >
                      {t('detail.extractPreview.yes')}
                    </Label>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='no' id='standard-rrm-depends-no' />
                    <Label
                      htmlFor='standard-rrm-depends-no'
                      className='cursor-pointer font-normal'
                    >
                      {t('detail.extractPreview.no')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            <div className='space-y-2'>
              <Label htmlFor='rrm-description'>
                {t('detail.rrmDescription')}
              </Label>
              <Textarea
                id='rrm-description'
                value={formData.description}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                rows={4}
              />
            </div>
          </div>

          <DialogFooter className='flex-row justify-between sm:justify-between'>
            <div>
              {isEdit && (
                <Button
                  type='button'
                  variant='destructive'
                  onClick={deleteCurrentRrm}
                  disabled={updateRiskReductionMeasures.isPending}
                >
                  {t('detail.deleteRiskReductionMeasure')}
                </Button>
              )}
            </div>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={closeDialog}
                disabled={updateRiskReductionMeasures.isPending}
              >
                {t('detail.cancel')}
              </Button>
              <Button
                onClick={isEdit ? updateRrm : createRrm}
                disabled={isSaveDisabled}
              >
                {updateRiskReductionMeasures.isPending
                  ? 'Saving…'
                  : t('detail.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
